// Single-use, module-scoped handoff so the editor (UploadFloor.runBackToGallery)
// can pass a freshly-captured gallery preview blob URL to the gallery
// (LayoutVersionSlider) synchronously through a route navigation.
//
// The gallery reads it during its useState lazy init so the user's edits appear
// on the very first paint, instead of waiting one tick for the cache useEffect
// to upgrade from the previous server image to the local blob.
//
// Entries are consumed (deleted) on read so a stale handoff cannot survive
// past the single navigation that wrote it. The durable cache (utils/Cache.js)
// is still written by the editor as a backup, so a later remount of the
// gallery (e.g. chip tap) still finds the blob via the existing cache effect.
//
// This module is intentionally NOT React state. The editor unmounts during
// the navigation, so we cannot rely on shared component state. A plain
// module-scoped Map survives that unmount cleanly.

import { mfLogGalleryStale, summarizeImageUrl } from "./galleryStaleTransitionDebug";
//
// --- Done flow (layout-keyed) ---
// runBackToGallery writes the blob here keyed by layoutId ONLY (not version),
// so the gallery's lazy init can read it WITHOUT needing location.state or
// any other React Router state that may not yet be committed during the first
// concurrent render. This is separate from the (layoutId, versionKey)-keyed
// duplicate flow below.
//
// Reads are split into peek + consume because BrowserRouter runs with
// v7_startTransition. runBackToGallery fires the GlobalContext setters at
// default priority and then navigate(), which is wrapped in startTransition.
// React commits the default-priority lane first, so SketchUpload swaps the
// editor for the gallery while the URL is still the editor's — mount 1. The
// route change then commits, and because /floor-plan/:layoutId/version/:version
// and /floor-gallery/:galleryId are separate <Route> entries each rendering
// their own <SketchUpload />, that unmounts the whole subtree and mounts a
// fresh one — mount 2, the gallery the user actually looks at.
//
// Consuming during the lazy init meant mount 1 burned the entry and mount 2
// started with nothing, which armed postEditFreshGate and put a spinner over
// a plan we already had the pixels for. peek() is therefore side-effect free
// and safe to call from a lazy init; consume() runs from a mount effect, once
// a render has committed. This mirrors pendingShuffleIntent, which needed the
// same split for the same reason in the opposite direction.
//
// TTL'd so an entry written by a navigation that never reached the gallery
// cannot survive to pin a blob onto a later, unrelated gallery visit. The
// durable cache (utils/Cache.js) still backs every later mount.
const LAST_DONE_TTL_MS = 30000;

let _lastDoneEntry = null; // { layoutId: string, at: number, entry: {...} }

const matchesLastDone = (layoutId) => {
  if (!_lastDoneEntry || !layoutId) return false;
  if (Date.now() - _lastDoneEntry.at > LAST_DONE_TTL_MS) {
    _lastDoneEntry = null;
    return false;
  }
  return _lastDoneEntry.layoutId === String(layoutId);
};

export const setLastDoneEntry = (layoutId, entry) => {
  _lastDoneEntry = { layoutId: String(layoutId), at: Date.now(), entry };
  mfLogGalleryStale("handoff.setLastDoneEntry", {
    layoutId: String(layoutId),
    version: entry?.version ?? null,
    url: summarizeImageUrl(entry?.url),
    cachedAt: entry?.cachedAt ?? null,
  });
};

/** Side-effect free: safe to call during render / useState lazy init. */
export const peekLastDoneEntry = (layoutId) => {
  if (!matchesLastDone(layoutId)) {
    mfLogGalleryStale("handoff.peekLastDoneEntry.miss", {
      layoutId: String(layoutId),
      slotLayoutId: _lastDoneEntry?.layoutId ?? null,
    });
    return null;
  }
  return _lastDoneEntry.entry;
};

/** Claims the entry. Call from a mount effect, after a render has committed. */
export const consumeLastDoneEntry = (layoutId) => {
  if (!matchesLastDone(layoutId)) {
    mfLogGalleryStale("handoff.consumeLastDoneEntry.miss", {
      layoutId: String(layoutId),
      slotLayoutId: _lastDoneEntry?.layoutId ?? null,
    });
    return null;
  }
  const entry = _lastDoneEntry.entry;
  _lastDoneEntry = null;
  mfLogGalleryStale("handoff.consumeLastDoneEntry.hit", {
    layoutId: String(layoutId),
    version: entry?.version ?? null,
    url: summarizeImageUrl(entry?.url),
    cachedAt: entry?.cachedAt ?? null,
  });
  return entry;
};

export const clearLastDoneEntry = () => {
  _lastDoneEntry = null;
};

// Sentinel value the editor writes when it intentionally skipped a cache
// write — e.g. the DOM-readiness gate timed out, the captured blob failed
// the size guard, or the world-geometry hash tripwire fired between the
// pre-POST capture and the cache-write step in handleDuplicateVersionOrLayout.
//
// The gallery (PR 2) reads the sentinel to discriminate three cases:
//   1. real entry  → render the captured blob immediately
//   2. CAPTURE_SKIPPED → arm an explicit loader rather than showing the
//                       stale server image while regeneration completes
//   3. null → no handoff happened (cold load, refresh, chip tap, etc.)
//
// String-typed (not Symbol) so it survives any accidental serialization
// path, but value is unique enough that no real blob URL can collide.
export const CAPTURE_SKIPPED = "__MF_CAPTURE_SKIPPED__";

const pending = new Map();

const buildKey = (layoutId, versionKey) =>
  `${layoutId == null ? "" : String(layoutId)}/${
    versionKey == null ? "" : String(versionKey)
  }`;

export const setPendingEditorBlob = (layoutId, versionKey, url) => {
  if (!layoutId || !versionKey || !url) return;
  if (url === CAPTURE_SKIPPED) {
    // Store the sentinel as the entire entry — no url/cachedAt fields, just
    // an unambiguous marker the consumer can identity-check.
    pending.set(buildKey(layoutId, versionKey), CAPTURE_SKIPPED);
    return;
  }
  pending.set(buildKey(layoutId, versionKey), {
    url,
    version: versionKey,
    cachedAt: Date.now(),
  });
};

export const consumePendingEditorBlob = (layoutId, versionKey) => {
  if (!layoutId || !versionKey) return null;
  const key = buildKey(layoutId, versionKey);
  const entry = pending.get(key);
  if (entry === undefined) return null;
  pending.delete(key);
  // Return the sentinel as-is so callers can discriminate between
  // "capture intentionally skipped" and "real blob handoff".
  if (entry === CAPTURE_SKIPPED) return CAPTURE_SKIPPED;
  return entry || null;
};
